import { z } from 'zod/v3';
import {
  BriefingDoneItemSchema,
  BriefingFindingSchema,
  BriefingProgramSummarySchema,
  BriefingTaskOverviewSchema,
} from './daily-briefing.dto';

export const InboxItemTypeSchema = z.enum([
  // Agent events
  'agent_escalated',
  // Retained for legacy rows; no longer created (Multica rule: completion is
  // visible from the status change instead of an info-level inbox row).
  'agent_resolved',
  'agent_batch_complete',
  // Auditor center
  'auditor_comment',
  'auditor_api_token_minted',
  // CES task events (Multica-style)
  'assigned',
  'unassigned',
  'assignee_changed',
  'mentioned',
  'new_comment',
  'status_changed',
  'due_date_changed',
  'task_reminder',
  'task_weekly_digest',
  // Policy approval workflow
  'policy_approval_requested',
  'policy_approved',
  'policy_returned_for_changes',
  // RFC-180 M4 — Access review reviewer tasks (per-principal + bulk-attested).
  // One row per AccessReviewItem, with `targetType='access_review_item'` and
  // `targetId=accessReviewItem.id`. Severity derives from item priority:
  // P0 → action_required, P1 → action_required, P2 → attention.
  'access_review_item',
  // Test (CES) approval workflow
  'test_approval_requested',
  'test_approved',
  'test_returned_for_changes',
  // Vendor assessment review workflow (CC9.2): submit → reviewer; decide → submitter.
  'vendor_assessment_review_requested',
  'vendor_assessment_decided',
  // Risk assessment review workflow (CC3.x): submit → reviewer; decide → submitter.
  'risk_assessment_review_requested',
  'risk_assessment_decided',
  // Integration health: a connected provider's credentials stopped working
  // (persistent auth error) and its automated checks were disabled.
  'integration_disconnected',
  // Integration → evidence reconcile (RFC-197) terminal outcome: the API-check
  // CES sync after a connection change finished — surfaced whether it created
  // evidence checks, failed to run, or could not even be enqueued (so the
  // previously-silent best-effort path is now observable).
  'integration_evidence_synced',
  // Evidence workflow: AI suggested a vault document for a test and needs human confirmation.
  'evidence_suggested',
  // RFC-190 loop notifications
  'test_regressed',
  'retest_due',
  'document_review_due',
  'evidence_expired',
  'compliance_exception_expiry',
  'finding_escalated',
  'check_quarantined',
  'sla_violation',
  'daily_briefing',
]);
export type InboxItemType = z.infer<typeof InboxItemTypeSchema>;

export const InboxSeveritySchema = z.enum([
  'action_required',
  'attention',
  'info',
]);
export type InboxSeverity = z.infer<typeof InboxSeveritySchema>;

export const InboxActorTypeSchema = z.enum(['user', 'agent', 'system']);
export type InboxActorType = z.infer<typeof InboxActorTypeSchema>;

export const InboxItemDTOSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  type: InboxItemTypeSchema,
  severity: InboxSeveritySchema,
  title: z.string(),
  body: z.string().nullable(),
  actorType: InboxActorTypeSchema.nullable(),
  actorName: z.string().nullable(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  targetName: z.string().nullable(),
  read: z.boolean(),
  archived: z.boolean(),
  details: z.record(z.unknown()).nullable(),
});
export type InboxItemDTO = z.infer<typeof InboxItemDTOSchema>;

export const InboxDailyBriefingSnapshotSchema = z.object({
  date: z.string(),
  overall: z.number(),
  delta: z.number().optional(),
  findings: z.array(BriefingFindingSchema),
  findingsTruncated: z.boolean().optional(),
  doneToday: z.array(BriefingDoneItemSchema),
  programs: z.array(BriefingProgramSummarySchema),
  tasks: BriefingTaskOverviewSchema.optional(),
});

export const InboxDailyBriefingDetailsSchema = z.object({
  briefing: InboxDailyBriefingSnapshotSchema,
});

export const InboxCesLifecycleStateSchema = z.enum([
  'actionable',
  'submitted_awaiting_review',
  'reassigned',
  'superseded',
  'unavailable',
]);
export type InboxCesLifecycleState = z.infer<typeof InboxCesLifecycleStateSchema>;

export const InboxResolvedContextDTOSchema = z.object({
  category: z.string(),
  id: z.string(),
  lifecycleState: InboxCesLifecycleStateSchema.optional(),
  title: z.string().nullable().optional(),
  submittedAt: z.date().nullable().optional(),
});
export type InboxResolvedContextDTO = z.infer<typeof InboxResolvedContextDTOSchema>;
